import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAgent, useCreateAgent, useUpdateAgent, useDeleteAgent } from '../hooks';
import { usePermissions } from '@/hooks/usePermissions';
import type { CreateAIAgentRequest } from '../services/agentsApi';
import { DEFAULT_AGENT_MODEL, type AIAgentCapabilityConfig, type AIAgentResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { Button } from '@/shared/components/ui/Button';
import { StatusPill, Tabs } from '@/shared/components/ui';
import { OverrideReasonDialog } from '../components/OverrideReasonDialog';

// Tab components (newly extracted)
import { BasicTab } from '../components/BasicTab';
import { AgentTestPanel } from '../components/AgentTestPanel';
import { BehaviorTab } from '../components/BehaviorTab';
import { TriggersTab } from '../components/TriggersTab';
import { ToolsTab } from '../components/ToolsTab';

// Tab components (already existed)
import { DatabasesTab } from '../components/DatabasesTab';
import { WebhooksTab } from '../components/WebhooksTab';
import { PreconditionsTab } from '../components/preconditions/PreconditionsTab';
import { AnalyticsTab } from '../components/analytics/AnalyticsTab';
import { GapsTab } from '../components/gaps/GapsTab';
import { HealthTab } from '../components/health/HealthTab';
import { ScenariosTab } from '../components/scenarios/ScenariosTab';
import { VersionsTab } from '../components/versions/VersionsTab';

// Modals
import { DeleteConfirmationModal } from '@/shared/components/ui/DeleteConfirmationModal';
import { AgentTrainModal } from '../components/training/AgentTrainModal';
import { PreDeployTestModal } from '../components/training/PreDeployTestModal';

type TabType = 'basic' | 'behavior' | 'triggers' | 'tools' | 'preconditions' | 'databases' | 'webhooks' | 'analytics' | 'health' | 'gaps' | 'versions' | 'scenarios';

/**
 * Abas horizontais no topo (Design System — antes eram uma sidebar vertical
 * `AgentSidebar`). Ordem e rótulos fiéis à tela real. `editOnly` reproduz o
 * gating original: monitoramento/testes (Saúde, Lacunas, Analytics, Cenários,
 * Versões) só aparecem em modo edição — dependem de `id` do agente salvo.
 */
const AGENT_TABS: { id: TabType; label: string; icon: IconName; editOnly: boolean }[] = [
  { id: 'basic', label: 'Informações Básicas', icon: 'cpu', editOnly: false },
  { id: 'behavior', label: 'Comportamento', icon: 'file-text', editOnly: false },
  { id: 'triggers', label: 'Gatilhos', icon: 'zap', editOnly: false },
  { id: 'tools', label: 'Ferramentas', icon: 'tool', editOnly: false },
  { id: 'preconditions', label: 'Pré-condições', icon: 'filter', editOnly: false },
  { id: 'databases', label: 'Base de dados', icon: 'database', editOnly: false },
  { id: 'webhooks', label: 'Webhooks', icon: 'link', editOnly: false },
  { id: 'health', label: 'Saúde', icon: 'activity', editOnly: true },
  { id: 'gaps', label: 'Lacunas', icon: 'search', editOnly: true },
  { id: 'analytics', label: 'Analytics', icon: 'trending-up', editOnly: true },
  { id: 'scenarios', label: 'Cenários', icon: 'layers', editOnly: true },
  { id: 'versions', label: 'Versões', icon: 'clock', editOnly: true },
];

export const AgentFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { data: agent, isLoading: isLoadingAgent } = useAgent(id);
  const createAgent = useCreateAgent();
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();
  const { hasModulePermission } = usePermissions();
  const canDeleteAgent = hasModulePermission('agents', 'delete');

  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [formData, setFormData] = useState<Partial<CreateAIAgentRequest>>({
    name: '',
    description: '',
    systemPrompt: '',
    model: DEFAULT_AGENT_MODEL,
    temperature: 0.7,
    maxTokens: 2000,
    delay: 12,
    triggers: {
      message_received: {
        enabled: false,
      },
      message_sent: {
        enabled: false,
      },
      lead_created: {
        enabled: false,
      },
    },
    customActionIds: [],
    skillWorkflowIds: [],
    databases: [],
    enabledCapabilities: [],
    voiceEnabled: false,
    voiceConfig: undefined,
  });

  // media_messaging nasce ativa e explícita em agentes NOVOS (spec
  // 2026-07-11-media-messaging-flag-design.md). Em modo edição o estado
  // inicia vazio: a hidratação preenche a partir do agente, e agente
  // legado sem entry permanece sem entry até o user tocar na box
  // (ausente = ativa — retrocompat).
  const [capabilityConfigs, setCapabilityConfigs] = useState<AIAgentCapabilityConfig[]>(
    isEditMode ? [] : [{ capability: 'media_messaging', enabled: true }],
  );

  const [originalPrompt, setOriginalPrompt] = useState<string>('');
  /**
   * Opção A — Test Before Deploy: model original pra detectar mudança de
   * modelo (um dos 2 triggers do teste pré-deploy, junto com prompt).
   */
  const [originalModel, setOriginalModel] = useState<string>('');

  /**
   * Opt-out do teste pré-deploy. Default `false` (teste roda
   * automaticamente quando o prompt/modelo muda — ver `shouldTestBeforeDeploy`).
   * Quando o admin marca, o save pula o `PreDeployTestModal` mas exige
   * justificativa (`OverrideReasonDialog`) que vira `X-Override-Reason`
   * no PATCH, driblando o `DeployGate` no backend.
   *
   * Preferência persistida em localStorage por usuário, não por agente.
   */
  const [skipPreDeployTest, setSkipPreDeployTest] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('skip-pre-deploy-test') === 'true';
  });

  /**
   * Estado do modal de Test Before Deploy. Fica aberto enquanto o cliente
   * passa pelos 5 estados do fluxo (choose-type → testing → results/error).
   */
  const [showPreDeployModal, setShowPreDeployModal] = useState(false);

  /**
   * Ref que carrega o `keepOnPage` do `handleSave` original até o callback
   * do `PreDeployTestModal` (onConfirmSave/onSkipTest), evitando stale
   * state — useState é re-renderizado mas no momento que o modal dispara
   * `onConfirmSave` precisamos o valor mais recente sem depender de closure.
   */
  const preDeployKeepOnPageRef = useRef<boolean>(false);
  // Id do agente cujos dados já foram carregados no formData (ver useEffect de load)
  const loadedAgentIdRef = useRef<string | null>(null);

  /**
   * Estado do dialog que pede justificativa quando o save tenta pular o
   * gate (admin marcou "Pular teste pré-deploy" OU aceitou regressão no
   * resultado do teste). A string virou header `X-Override-Reason` no PATCH.
   *
   * `keepOnPage` carrega no contexto se o save vai redirecionar ou ficar
   * na página — evita stale state ao chamar `performSave` depois.
   */
  const [overrideDialog, setOverrideDialog] = useState<{
    open: boolean;
    context: 'skip-test' | 'accept-regression';
    keepOnPage: boolean;
  }>({ open: false, context: 'skip-test', keepOnPage: false });

  // Estado para controle do modal de treinamento.
  // A partir da Sprint UI-2, o AgentTrainModal encapsula TODO o fluxo
  // (escolha de tipo → smoke test → Prompt Coach → diff) — não precisa
  // mais de isTraining/trainError/suggestedPrompt aqui; o modal gerencia
  // internamente via hooks useStartTraining / useSnapshot / etc.
  const [showTrainModal, setShowTrainModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Mobile: bottom-sheet do "Testar agente" (#210 — o painel aside é hidden lg:flex)
  const [showTestSheet, setShowTestSheet] = useState(false);

  const handleConfirmDelete = async () => {
    if (!id) return;
    try {
      await deleteAgent.mutateAsync(id);
      setShowDeleteModal(false);
      navigate('/automation/ai-agents');
    } catch {
      // erro já tratado pelo hook (toast)
    }
  };

  /**
   * Sprint UI-4: quando o cliente aplica sugestões do AgentTrainModal,
   * guardamos aqui o ID do snapshot que originou o novo prompt. Na hora
   * de salvar o agente, esse ID é enviado no body como `baseSnapshotId`
   * — o backend grava na nova versão, permitindo que a tab "Versões"
   * exiba o score herdado do treino.
   *
   * Limpado após o salvamento OU quando o cliente edita manualmente o
   * prompt (aí a vinculação ao snapshot perde o sentido).
   */
  const [pendingBaseSnapshotId, setPendingBaseSnapshotId] = useState<
    string | null
  >(null);

  // Contagem de lacunas não resolvidas (reportada pelo GapsTab quando carrega)
  const [gapsCount, setGapsCount] = useState(0);

  // Handler para mudanças de configuração de capacidades
  const handleCapabilityConfigChange = (config: AIAgentCapabilityConfig) => {
    setCapabilityConfigs(prev => {
      const existing = prev.findIndex(c => c.capability === config.capability);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = config;
        return updated;
      }
      return [...prev, config];
    });
  };

  // Load agent data in edit mode.
  // Guarda por id: só popular o form quando o AGENTE carregado muda, não a
  // cada refetch da query. Sem isto, as mutations de webhook (que invalidam
  // ['ai-agent', id] pra aba Webhooks refletir o salvamento imediato)
  // repopulariam o formData e apagariam silenciosamente edições não salvas
  // de outras abas (systemPrompt, nome, triggers...).
  useEffect(() => {
    if (isEditMode && agent) {
      if (loadedAgentIdRef.current === agent.id) return;
      loadedAgentIdRef.current = agent.id;
      setFormData({
        name: agent.name,
        description: agent.description,
        systemPrompt: agent.systemPrompt,
        model: agent.model,
        temperature: agent.temperature,
        maxTokens: agent.maxTokens,
        delay: agent.delay ?? 12,
        // Sprint de Cenários: carregar category + segment do agente pra
        // popular os selects em modo edição. Sem isto, os selects ficam
        // vazios mesmo quando o agente já tem valores salvos E o save
        // envia `undefined` por cima, apagando os valores anteriores.
        // Uso de conditional spread pra manter exactOptionalPropertyTypes
        // feliz (só inclui a chave quando tem valor).
        ...(agent.category ? { category: agent.category } : {}),
        ...(agent.segment ? { segment: agent.segment } : {}),
        triggers: agent.triggers,
        customActionIds: agent.customActionIds,
        skillWorkflowIds: agent.skillWorkflowIds || [],
        ...(agent.toolPreconditions ? { toolPreconditions: agent.toolPreconditions } : {}),
        // Sem carregar estes dois, a UI mente em modo edição: a seção de
        // escalação mostrava "Modelo decide" com equipe salva, e o cadastro
        // oficial apareceria vazio com ajuste salvo (Fase 3, 2026-08-25).
        ...(agent.escalationConfig ? { escalationConfig: agent.escalationConfig } : {}),
        ...(agent.officialInfoOverride ? { officialInfoOverride: agent.officialInfoOverride } : {}),
        databases: agent.databases || [],
        enabledCapabilities: agent.enabledCapabilities,
        voiceEnabled: agent.voiceEnabled || false,
        voiceConfig: agent.voiceConfig,
      });

      // Guardar prompt original para comparação no diff
      setOriginalPrompt(agent.systemPrompt || '');
      // Opção A: guarda model original pra detectar mudança no save
      setOriginalModel(agent.model || '');

      // Carregar enabledCapabilities (já são AIAgentCapabilityConfig[])
      if (agent.enabledCapabilities && agent.enabledCapabilities.length > 0) {
        setCapabilityConfigs(agent.enabledCapabilities);
      }
    }
  }, [isEditMode, agent]);

  // Verifica se o prompt foi alterado (para exibir modal de diff)
  const hasPromptChanged = isEditMode &&
    originalPrompt !== '' &&
    formData.systemPrompt !== originalPrompt;

  /**
   * Opção A — Test Before Deploy: detecta se o cliente mudou o modelo
   * (segundo trigger do teste pré-deploy junto com o prompt).
   */
  const hasModelChanged = isEditMode &&
    originalModel !== '' &&
    formData.model !== originalModel;

  // Handler para iniciar o processo de salvamento.
  //   - `keepOnPage`: ao concluir, fica na página (toast) em vez de redirect
  //   - `newPromptOverride`: usado pelo `AgentTrainModal.onApplyPrompt` —
  //     o callback chama `setFormData({systemPrompt: newPrompt})` e logo em
  //     seguida `handleSave`. Como setState é assíncrono, `formData.systemPrompt`
  //     ainda é o antigo aqui. Sem o override, `hasPromptChanged` retornaria
  //     false e o save bypassaria o pre-deploy gate, falhando depois com 422.
  const handleSave = async (
    opts: { keepOnPage?: boolean; newPromptOverride?: string } = {},
  ) => {
    const keepOnPage = opts.keepOnPage ?? false;

    // Recalcula needsPreDeployGuard com o override quando aplicável
    const effectivePrompt = opts.newPromptOverride ?? formData.systemPrompt ?? '';
    const promptChanged =
      isEditMode && originalPrompt !== '' && effectivePrompt !== originalPrompt;
    const guardNeeded = isEditMode && (promptChanged || hasModelChanged);

    if (guardNeeded) {
      if (skipPreDeployTest) {
        // Admin marcou opt-out → pede justificativa (vira X-Override-Reason).
        // `keepOnPage` viaja junto no contexto do dialog pra evitar stale state.
        setOverrideDialog({ open: true, context: 'skip-test', keepOnPage });
        return;
      }
      // Caminho padrão: roda Test Before Deploy automaticamente.
      // `keepOnPage` persiste enquanto o modal está aberto via ref pra
      // recuperar nos callbacks do PreDeployTestModal sem stale state.
      preDeployKeepOnPageRef.current = keepOnPage;
      setShowPreDeployModal(true);
      return;
    }

    // Sem mudança que dispare gate → salva direto
    await performSave({ keepOnPage });
  };

  // Função que realiza o salvamento de fato. Todos os parâmetros vêm via
  // argumento — NUNCA lê state que pode estar stale (ex: pendingBaseSnapshotId
  // recém-setado no mesmo handler). Sintoma histórico de stale state:
  // baseSnapshotId no PATCH não bater com o snapshot citado no
  // X-Override-Reason (incidente 2026-05-13).
  const performSave = async (
    opts: {
      overrideReason?: string;
      /** ID do snapshot pré-deploy a vincular nesta versão (override-vivo do estado). */
      baseSnapshotId?: string | null;
      /** Se true, mantém na página após sucesso (sem redirect). */
      keepOnPage?: boolean;
    } = {},
  ) => {
    try {
      // Resolve baseSnapshotId: param explícito > state pendente. Param
      // tem precedência porque é passado pelo callback do mesmo handler
      // que acabou de gerar o snapshotId — não passou ainda pelo re-render.
      const resolvedSnapshotId =
        opts.baseSnapshotId !== undefined ? opts.baseSnapshotId : pendingBaseSnapshotId;

      // Enviar enabledCapabilities como array de objetos AIAgentCapabilityConfig.
      // media_messaging é exceção ao filtro: precisa persistir enabled:false
      // explícito — ausência significa "ativa" (retrocompat), então descartar
      // a entry desabilitada tornaria impossível desligar o envio de mídia.
      const enabledConfigs = capabilityConfigs.filter(
        c => c.enabled || c.capability === 'media_messaging',
      );
      const dataToSave = {
        ...formData,
        enabledCapabilities: enabledConfigs,
        ...(resolvedSnapshotId ? { baseSnapshotId: resolvedSnapshotId } : {}),
      };

      if (isEditMode && id) {
        await updateAgent.mutateAsync({
          id,
          data: dataToSave,
          ...(opts.overrideReason ? { overrideReason: opts.overrideReason } : {}),
        });
        // Snapshot do prompt original atualiza pra refletir o que está
        // salvo agora — evita re-disparar o teste pra um prompt que já foi
        // persistido (relevante quando `keepOnPage=true`).
        setOriginalPrompt(dataToSave.systemPrompt || '');
        setOriginalModel(dataToSave.model || '');
      } else {
        await createAgent.mutateAsync(dataToSave as CreateAIAgentRequest);
      }

      // Limpa a referência ao snapshot — próximo save começa do zero
      setPendingBaseSnapshotId(null);

      if (opts.keepOnPage) {
        // Toast já é emitido pelo `useUpdateAgent.onSuccess` ("Agente
        // atualizado com sucesso!"). Aqui só garantimos não redirecionar.
        return;
      }
      navigate('/automation/ai-agents');
    } catch (error) {
      // Error already handled by hooks
    }
  };

  // Handler do dialog de justificativa (skip-test ou accept-regression)
  const handleOverrideConfirm = async (reason: string) => {
    const keepOnPage = overrideDialog.keepOnPage;
    setOverrideDialog({ open: false, context: 'skip-test', keepOnPage: false });
    await performSave({ overrideReason: reason, keepOnPage });
  };

  // Após `useUpdateAgent.onError`, o toast genérico não diferencia gate
  // do resto. Se o erro for 422 do DeployGate, mostramos um toast extra
  // explicativo. O hook tradicional não passa o status pra cá, então
  // dependemos do error.message (que inclui o `code` do backend AppError).
  // Implementação simples: detectar via regex no error e oferecer ação.
  useEffect(() => {
    const err = updateAgent.error;
    if (!err) return;
    const msg = err.message || '';
    if (msg.includes('deploy_gate_required') || msg.includes('regression_detected')) {
      toast.warning(
        'Rode o teste pré-deploy antes de salvar, ou marque "Pular teste pré-deploy" para justificar a publicação direta.',
        { duration: 6000 },
      );
    }
  }, [updateAgent.error]);

  const canSave = formData.name && formData.systemPrompt;
  const isSaving = createAgent.isPending || updateAgent.isPending;

  const tabBadges: Partial<Record<TabType, number>> = {
    triggers: Object.values(formData.triggers ?? {}).filter(t => t.enabled).length,
    tools: (formData.customActionIds?.length ?? 0) + (formData.skillWorkflowIds?.length ?? 0),
    preconditions: formData.toolPreconditions?.length ?? 0,
    databases: formData.databases?.length ?? 0,
    webhooks: agent?.webhooks?.length ?? 0,
    gaps: gapsCount,
  };

  const visibleTabs = AGENT_TABS.filter(t => !t.editOnly || isEditMode);

  if (isLoadingAgent && isEditMode) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <p style={{ color: 'var(--theme-card-text)' }}>Carregando agente...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ===== Header sticky: breadcrumb + status + Treinar + Excluir + Salvar =====
          (borda inferior vem da barra de abas — Tabs pageLevel, F55) */}
      <div
        className="flex-shrink-0"
        style={{ backgroundColor: 'var(--theme-card-background)' }}
      >
        <div className="flex items-center gap-3 px-4 sm:px-6 md:px-8 py-3">
          {/* Breadcrumb (nunca seta-voltar) + status */}
          <nav className="flex items-center gap-1.5 min-w-0 text-sm" aria-label="breadcrumb">
            <button
              onClick={() => navigate('/automation/ai-agents')}
              className="font-medium transition-colors flex-shrink-0 hidden sm:inline-flex"
              style={{ color: 'var(--theme-muted-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-card-text)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
              data-testid="agent-form-breadcrumb-root"
            >
              Agentes de IA
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 flex-shrink-0 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <div className="min-w-0">
              <span className="font-semibold truncate block leading-tight" style={{ color: 'var(--theme-card-text)' }} data-testid="agent-form-title">
                {isEditMode ? (formData.name || 'Editar Agente') : 'Novo Agente'}
              </span>
              {/* Subtítulo mobile (mockup .m-appbar .t2 = "Agentes de IA · <status>") */}
              <p className="sm:hidden text-[11px] truncate leading-none mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                Agentes de IA{isEditMode && agent ? (
                  <> · <span style={{ color: agent.status === 'active' ? 'var(--theme-success-color)' : 'var(--theme-muted-text)', fontWeight: 600 }}>{agent.status === 'active' ? 'Ativo' : 'Inativo'}</span></>
                ) : null}
              </p>
            </div>
          </nav>

          {isEditMode && agent && (
            <StatusPill
              variant={agent.status === 'active' ? 'success' : 'neutral'}
              label={agent.status === 'active' ? 'Ativo' : 'Inativo'}
              className="hidden sm:inline-flex flex-shrink-0"
              data-testid="agent-form-status"
            />
          )}

          <div className="flex-1" />

          {/* Ações — Treinar/Excluir só no desktop (mockup mobile = só Salvar) */}
          {isEditMode && (
            <div className="hidden sm:block">
              <Button
                variant="secondary"
                size="md"
                icon="cpu"
                onClick={() => setShowTrainModal(true)}
                data-testid="agent-form-train-button"
              >
                Treinar agente
              </Button>
            </div>
          )}

          {isEditMode && canDeleteAgent && (
            <div className="hidden sm:block">
              <Button
                variant="ghost"
                size="md"
                onClick={() => setShowDeleteModal(true)}
                disabled={deleteAgent.isPending}
                title="Excluir agente"
                style={{ color: 'var(--theme-error-color)' }}
                data-testid="agent-form-delete-button"
              >
                <Icon name="trash" className="w-4 h-4" />
              </Button>
            </div>
          )}

          <Button
            variant="primary"
            size="md"
            icon="save"
            onClick={() => handleSave()}
            disabled={!canSave || isSaving}
            loading={isSaving}
            data-testid="agent-form-save-button"
          >
            {isEditMode ? 'Salvar' : 'Criar'}
          </Button>
        </div>

        {/* ===== Abas horizontais (F55 — Tabs shared pageLevel; rolam no mobile) ===== */}
        <Tabs
          pageLevel
          fullBleed
          variant="underline"
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as TabType)}
          tabs={visibleTabs.map(tab => ({
            id: tab.id,
            label: <span data-testid={`agent-form-tab-${tab.id}`}>{tab.label}</span>,
            icon: tab.icon,
            badge: tabBadges[tab.id] ?? 0,
            content: null,
          }))}
        />
      </div>

      {/* ===== Conteúdo das abas + painel "Testar agente" fixo à direita (mockup agent-form-03) ===== */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {/* Tab: Informações Básicas */}
          {activeTab === 'basic' && <BasicTab formData={formData} setFormData={setFormData} />}

          {/* Tab: Comportamento */}
          {activeTab === 'behavior' && (
            <BehaviorTab
              formData={formData}
              setFormData={setFormData}
              isEditMode={isEditMode}
              agentId={id}
              hasPromptChanged={hasPromptChanged}
              skipPreDeployTest={skipPreDeployTest}
              onSkipPreDeployTestChange={(checked) => {
                setSkipPreDeployTest(checked);
                localStorage.setItem('skip-pre-deploy-test', String(checked));
              }}
            />
          )}

          {/* Tab: Gatilhos */}
          {activeTab === 'triggers' && <TriggersTab formData={formData} setFormData={setFormData} />}

          {/* Tab: Ferramentas */}
          {activeTab === 'tools' && (
            <ToolsTab
              formData={formData}
              setFormData={setFormData}
              capabilityConfigs={capabilityConfigs}
              onCapabilityConfigChange={handleCapabilityConfigChange}
            />
          )}

          {/* Tab: Pré-condições */}
          {activeTab === 'preconditions' && (
            <PreconditionsTab
              agent={{
                customActionIds: (agent?.customActionIds ?? formData.customActionIds) || [],
                enabledCapabilities: (agent?.enabledCapabilities ?? formData.enabledCapabilities) || [],
                databases: (agent?.databases ?? formData.databases) || [],
                skillWorkflowIds: agent?.skillWorkflowIds ?? formData.skillWorkflowIds,
                toolPreconditions: agent?.toolPreconditions ?? formData.toolPreconditions,
              } as AIAgentResponse}
              formData={formData}
              onChange={(toolPreconditions) =>
                setFormData((prev) => ({ ...prev, toolPreconditions }))
              }
            />
          )}

          {/* Tab: Databases */}
          {activeTab === 'databases' && (
            <div className="space-y-6 max-w-3xl">
              <DatabasesTab
                selectedDatabases={formData.databases || []}
                onChange={(databases) => setFormData({ ...formData, databases })}
              />
            </div>
          )}

          {/* Tab: Webhooks */}
          {activeTab === 'webhooks' && (
            <div className="space-y-6 max-w-3xl">
              <WebhooksTab agentId={isEditMode ? id : undefined} />
            </div>
          )}

          {/* Tab: Saúde (só em modo edição) */}
          {activeTab === 'health' && isEditMode && id && (
            <div className="space-y-6">
              <HealthTab
                agentId={id}
                onTrainAgent={() => setShowTrainModal(true)}
                onViewGaps={() => setActiveTab('gaps')}
              />
            </div>
          )}

          {/* Tab: Lacunas (só em modo edição) */}
          {activeTab === 'gaps' && isEditMode && id && (
            <div className="space-y-6">
              <GapsTab agentId={id} agentDatabaseIds={formData.databases || []} onCountChange={setGapsCount} />
            </div>
          )}

          {/* Tab: Cenários (só em modo edição) */}
          {activeTab === 'scenarios' && isEditMode && id && (
            <div className="space-y-6">
              <ScenariosTab
                {...(formData.category ? { agentCategory: formData.category } : {})}
                {...(formData.segment ? { agentSegment: formData.segment } : {})}
              />
            </div>
          )}

          {/* Tab: Versões (só em modo edição) */}
          {activeTab === 'versions' && isEditMode && id && (
            <div className="space-y-6">
              <VersionsTab agentId={id} currentPrompt={formData.systemPrompt || ''} />
            </div>
          )}

          {/* Tab: Analytics (só em modo edição) */}
          {activeTab === 'analytics' && isEditMode && id && (
            <div className="space-y-6">
              <AnalyticsTab agentId={id} />
            </div>
          )}
        </div>
      </div>

      {/* Painel "Testar agente" — só em edição (a simulação precisa do agente salvo) */}
      {isEditMode && id && <AgentTestPanel agentId={id} model={String(formData.model || '')} />}
      </div>

      {/* Modal de Treinamento com IA (fluxo de 4 passos com smoke test + Prompt Coach).
          O TrainModal tem seu próprio step `'diff'` interno que mostra o diff
          do prompt antes de chamar `onApplyPrompt` — então quando este callback
          dispara, o usuário JÁ aprovou o diff. Não abrimos outro modal de diff
          aqui (era redundante — exibia a mesma comparação). Disparamos o save
          direto, que vai cair em PreDeployTestModal (default) ou OverrideDialog
          (se admin marcou pular teste). */}
      <AgentTrainModal
        isOpen={showTrainModal}
        agentId={id}
        onClose={() => setShowTrainModal(false)}
        onApplyPrompt={(newPrompt, baseSnapshotId) => {
          setFormData({ ...formData, systemPrompt: newPrompt });
          setPendingBaseSnapshotId(baseSnapshotId);
          // Save imediato. Passamos `newPromptOverride` pois `setFormData`
          // é async — handleSave precisa do prompt novo pra calcular
          // `hasPromptChanged` corretamente. `keepOnPage: true` mantém o
          // admin no editor após o save (toast só).
          void handleSave({ keepOnPage: true, newPromptOverride: newPrompt });
        }}
      />

      {/* Test Before Deploy automático — abre toda vez que o save dispara
          e o prompt/modelo mudou (a menos que admin tenha marcado pular). */}
      {id && (
        <PreDeployTestModal
          isOpen={showPreDeployModal}
          agentId={id}
          newPrompt={formData.systemPrompt || ''}
          {...(hasModelChanged ? { newModel: formData.model } : {})}
          onConfirmSave={(snapshotId, overrideReason) => {
            // Cliente aprovou o teste — liga a nova versão ao snapshot
            // pré-deploy via baseSnapshotId. Passamos `baseSnapshotId`
            // DIRETO como param do performSave (evita stale state do
            // setState assíncrono). Se aceitou regressão (danger),
            // `overrideReason` vem preenchido e vira X-Override-Reason.
            setPendingBaseSnapshotId(snapshotId);
            setShowPreDeployModal(false);
            void performSave({
              baseSnapshotId: snapshotId,
              keepOnPage: preDeployKeepOnPageRef.current,
              ...(overrideReason ? { overrideReason } : {}),
            });
          }}
          onCancel={() => setShowPreDeployModal(false)}
          onSkipTest={() => {
            // Cliente escolheu pular o teste durante o fluxo — pede
            // justificativa pra atender o gate via X-Override-Reason.
            setShowPreDeployModal(false);
            setOverrideDialog({
              open: true,
              context: 'skip-test',
              keepOnPage: preDeployKeepOnPageRef.current,
            });
          }}
        />
      )}

      {/* Dialog que captura justificativa do override (X-Override-Reason).
          Aberto em 2 cenários:
            1. Admin marcou "Pular teste pré-deploy" no BehaviorTab
            2. Admin clicou "Pular teste" durante o PreDeployTestModal */}
      <OverrideReasonDialog
        isOpen={overrideDialog.open}
        context={overrideDialog.context}
        onConfirm={handleOverrideConfirm}
        onCancel={() => setOverrideDialog((prev) => ({ ...prev, open: false }))}
      />

      {/* Modal de confirmação de exclusão do agente */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Excluir agente"
        message={`Tem certeza que deseja excluir o agente "${formData.name || 'sem nome'}"?`}
        warningMessage="Esta ação não pode ser desfeita. Todas as configurações, bases de conhecimento vinculadas e histórico do agente serão perdidos."
        isLoading={deleteAgent.isPending}
      />

      {/* FAB "Testar agente" — mobile (o painel aside some < lg). Só em edição. */}
      {isEditMode && id && (
        <button
          type="button"
          onClick={() => setShowTestSheet(true)}
          className="lg:hidden fixed right-4 bottom-[84px] z-40 flex items-center gap-2 h-12 pl-4 pr-5 rounded-full text-white shadow-xl transition-colors"
          style={{ backgroundColor: 'var(--theme-button-primary)' }}
          data-testid="agent-test-fab"
          aria-label="Testar agente"
        >
          <Icon name="cpu" className="w-5 h-5" />
          <span className="text-sm font-semibold">Testar</span>
        </button>
      )}

      {/* Bottom-sheet do teste (mobile) */}
      {isEditMode && id && showTestSheet && (
        <AgentTestPanel
          agentId={id}
          model={String(formData.model || '')}
          variant="sheet"
          onClose={() => setShowTestSheet(false)}
        />
      )}

    </div>
  );
};
